import {
  SearchParamsSourceContextProvider,
  SearchParamsSourceValue,
} from '@aibees/shared/hooks';
import { memo, useCallback, useMemo, useState } from 'react';

/**
 * Isolates filter/sort/page state of AdvanceTables rendered inside the View Records
 * popup from the URL — and therefore from any AdvanceTable on the page behind it.
 *
 * Why it's needed:
 *   AdvanceTable's setFilter writes to the params source returned by
 *   useSearchParamsSource(). With no provider in the tree, that source is the
 *   URL (via useQueryStringState). The popup is mounted on top of pages like
 *   /search/exports which themselves render AdvanceTables bound to the same
 *   URL keys (`name`, `grouping`, `type`, `visibility`, ...). Typing a search
 *   query inside the popup would mutate `?name=...` on the URL, which the
 *   underlying page's table reads from — so its results silently changed too.
 *
 * Why it works:
 *   useSearchParamsSource reads SearchParamsSourceContext first and only falls
 *   back to URL when that context is null. By providing our own value with
 *   in-memory `params` plus setters that mutate that local state, every
 *   AdvanceTable inside this subtree reads and writes through us instead of
 *   the URL. The URL is never touched, so the page behind the popup is
 *   unaffected. State lives for the lifetime of the popup mount and is
 *   discarded when the popup closes — which is what we want, since the popup
 *   should open in a clean state each time.
 *
 * Scope:
 *   Mounted inside each <Styled.Panel>, so every main tab gets its own
 *   provider instance. MUI Lab TabPanel only renders the active panel's
 *   children (`value === context.value && children`), so switching tabs
 *   unmounts the inactive provider and discards its filter state. Reopening
 *   that tab starts fresh.
 */
type Params = Partial<Record<string, string>>;

interface Props {
  children: React.ReactNode;
}

export const PopupParamsProvider = memo(({ children }: Props) => {
  const [params, setParamsState] = useState<Params>({});

  const setParams = useCallback<SearchParamsSourceValue['setParams']>(
    (values) => {
      setParamsState(values ?? {});
    },
    []
  );

  const setParam = useCallback<SearchParamsSourceValue['setParam']>(
    (key, value) => {
      setParamsState((prev) => {
        const next = { ...prev };
        if (value === undefined || value === null || value === '') {
          delete next[key];
        } else {
          next[key] = value;
        }
        return next;
      });
    },
    []
  );

  const removeParam = useCallback<SearchParamsSourceValue['removeParam']>(
    (key) => {
      setParamsState((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
    },
    []
  );

  const value = useMemo<SearchParamsSourceValue>(
    () => ({ params, setParams, setParam, removeParam }),
    [params, setParams, setParam, removeParam]
  );

  return (
    <SearchParamsSourceContextProvider value={value}>
      {children}
    </SearchParamsSourceContextProvider>
  );
});

PopupParamsProvider.displayName = 'PopupParamsProvider';
